import React, { FunctionComponent, useState, useEffect } from "react";
import { Box } from "@mui/material";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { useAppSelector } from "src/app/hooks";

export interface HeightWeightCardData {
  operationDate: Date | null;
  operator: string;
  height: string;
  weight: string;
  bmi: string;
}

interface HeightWeightCardListProps {
  onDataChange?: (data: HeightWeightCardData) => void;
  initialData?: HeightWeightCardData | null;
}

const HeightWeightCardList: FunctionComponent<HeightWeightCardListProps> = ({
  onDataChange,
  initialData,
}) => {
  const { user } = useAppSelector((state) => (state as any).auth);

  const [operationDate, setOperationDate] = useState<Date | null>(
    initialData?.operationDate || new Date()
  );
  const [operator, setOperator] = useState<string>(
    initialData?.operator || ""
  );
  const [height, setHeight] = useState<string>(
    initialData?.height || ""
  );
  const [weight, setWeight] = useState<string>(
    initialData?.weight || ""
  );
  const [bmi, setBmi] = useState<string>(
    initialData?.bmi || ""
  );

  useEffect(() => {
    if (initialData) {
      setOperationDate(initialData.operationDate || new Date());
      setOperator(initialData.operator || "");
      setHeight(initialData.height || "");
      setWeight(initialData.weight || "");
      // BMI'yi sadece initialData'dan geliyorsa set et (hesaplanmış değilse)
      if (initialData.bmi && initialData.bmi !== bmi) {
        setBmi(initialData.bmi);
      }
    } else {
      // Veri yoksa sadece tarihi set et
      setOperationDate(new Date());
      setOperator("");
      setHeight("");
      setWeight("");
      setBmi("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.operationDate, initialData?.operator, initialData?.height, initialData?.weight]);

  useEffect(() => {
    if (user?.username && !operator) {
      setOperator(user.username);
    }
  }, [user, operator]);

  // VKİ hesaplama: Kilo (kg) / (Boy (cm) / 100)^2
  useEffect(() => {
    if (height && weight) {
      const heightInMeters = parseFloat(height) / 100;
      const weightInKg = parseFloat(weight);
      if (heightInMeters > 0 && weightInKg > 0) {
        const calculatedBmi = (weightInKg / (heightInMeters * heightInMeters)).toFixed(2);
        // Sadece değer değiştiyse set et (sonsuz döngüyü önlemek için)
        if (calculatedBmi !== bmi) {
          setBmi(calculatedBmi);
        }
      } else {
        if (bmi !== "") {
          setBmi("");
        }
      }
    } else {
      if (bmi !== "") {
        setBmi("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, weight]);

  useEffect(() => {
    if (onDataChange) {
      // BMI'yi hesapla ve gönder
      let calculatedBmi = bmi;
      if (height && weight) {
        const heightInMeters = parseFloat(height) / 100;
        const weightInKg = parseFloat(weight);
        if (heightInMeters > 0 && weightInKg > 0) {
          calculatedBmi = (weightInKg / (heightInMeters * heightInMeters)).toFixed(2);
        }
      }
      onDataChange({
        operationDate,
        operator,
        height,
        weight,
        bmi: calculatedBmi,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationDate, operator, height, weight]);

  return (
    <Box sx={{ width: "100%", position: "relative", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "22px", textAlign: "left", fontSize: "14px", color: "#111827", fontFamily: "Urbanist" }}>
      <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "12px" }}>

        <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: "20px" }}>
            <Box sx={{ width: "200px", position: "relative", lineHeight: "150%", fontWeight: 600 }}>İşlem Tarihi :</Box>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <ByzMuiDateTimePicker
                value={operationDate}
                onChange={(val) => setOperationDate(val as Date | null)}
                width="180px"
                disabled
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: "20px" }}>
            <Box sx={{ width: "200px", position: "relative", lineHeight: "150%", fontWeight: 600 }}>İşlem Yapan Kullanıcı :</Box>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <ByzMuiTextField
                label="İşlem Yapan Kullanıcı"
                value={operator}
                onChange={(e) => setOperator(e.target.value)}
                sx={{ width: "180px" }}
                cantChange
                noPlaceholder
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: "20px" }}>
            <Box sx={{ width: "200px", position: "relative", lineHeight: "150%", fontWeight: 600 }}>Boy(cm) :</Box>
            <ByzMuiTextField
              label="Boy(cm)"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              sx={{ width: "180px" }}
              type="number"
              noPlaceholder
            />
          </Box>
        </Box>

        <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: "20px" }}>
            <Box sx={{ width: "200px", position: "relative", lineHeight: "150%", fontWeight: 600 }}>Ağırlık(kg) :</Box>
            <ByzMuiTextField
              label="Ağırlık(kg)"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              sx={{ width: "180px" }}
              type="number"
              noPlaceholder
            />
          </Box>
        </Box>

        <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: "20px" }}>
            <Box sx={{ width: "200px", position: "relative", lineHeight: "150%", fontWeight: 600 }}>VKİ :</Box>
            <ByzMuiTextField
              label="VKİ"
              value={bmi}
              sx={{ width: "180px" }}
              disabled
              noPlaceholder
            />
          </Box>
        </Box>

      </Box>
    </Box>
  );
};

export default HeightWeightCardList;
